/**
 * 文本布局（M2-17 / DESIGN.md §2.5、§6.3）。
 *
 * 管线：整段 shaping 量宽 → 折行决策（禁则处理）→ 逐行重新 shaping 定位。
 * 「量宽一次、逐行重排」是折行引擎的标准做法：跨行边界的连字/字距
 * 在断行后本来就该失效，逐行重排恰好给出正确结果。
 *
 * CJK 规则（按 §2.5 的优先级实现前三条，规范来源 JLReq/CLReq）：
 * 1. **避头尾（禁则）**：行首禁则字符（。，、」等）不得出现在行首，
 *    行尾禁则（「（等）不得出现在行尾——断点向前回退直到合法。
 * 2. **标点挤压**：连续约物的第二个起advance 减半（全角标点自带的
 *    空白叠在一起太松）。
 * 3. **中西文间距**：CJK 与拉丁/数字相邻处加四分之一 em（四分空）。
 *
 * 输出是 glyph id + 位置的纯数据（可结构化克隆），同输入逐位同输出。
 * 坐标：**文本框局部空间**，原点左上，y 向下；每行 baseline 在
 * lineTop + ascent（ascent 取 0.88em——CJK 字体的常见默认）。
 */
import type { Shaper } from './shaper';

/** 行首禁则：不能出现在行首的字符（JLReq/CLReq 的常用子集 + 西文标点）。 */
export const KINSOKU_LINE_START = new Set(
  '、。，．：；！？）」』》〉】〕％…‰°′″℃々ー～·,.;:!?)]%’”',
);

/** 行尾禁则：不能出现在行尾的字符。 */
export const KINSOKU_LINE_END = new Set('（「『《〈【〔([‘“');

/** 约物（标点挤压的对象）：全角标点。 */
const FULLWIDTH_PUNCTUATION = new Set('、。，．：；！？（）「」『』《》〈〉【】〔〕·…—');

const CJK_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x4e00, 0x9fff], // CJK 统一表意
  [0x3400, 0x4dbf],
  [0x3040, 0x30ff], // 假名
  [0xf900, 0xfaff],
];

export function isCjk(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return CJK_RANGES.some(([lo, hi]) => code >= lo && code <= hi);
}

export function isLatinOrDigit(char: string): boolean {
  return /[A-Za-z0-9]/.test(char);
}

export interface LayoutOptions {
  /** 字号（像素/em）。 */
  readonly fontSize: number;
  /** 行高倍数（相对字号）。§7.3 中文正文建议 1.6–1.75。 */
  readonly lineHeight: number;
  /** 段落文本的容器宽（像素）。省略 = 点文本，不折行。 */
  readonly maxWidth?: number;
}

export interface PositionedGlyph {
  readonly glyphId: number;
  /** 文本框局部空间像素，glyph 原点（baseline 左端）。 */
  readonly x: number;
  readonly y: number;
  /** 源文本字符下标。 */
  readonly cluster: number;
  /**
   * 这个字形来自 `spans` 里的第几个 span（0-based）——富文本混合格式
   * （M4-38）需要知道每个字形该用哪个 span 自己的字体/颜色栅格化；
   * 单 shaper 的旧调用（`layoutText`/`breakLines`）只有一个隐式 span，
   * 恒为 0，兼容既有调用方（它们从不读这个字段）。
   */
  readonly spanIndex: number;
}

export interface LayoutLine {
  /** 源文本区间 [start, end)。 */
  readonly start: number;
  readonly end: number;
  /** 行宽（像素，含挤压与四分空）。 */
  readonly width: number;
}

export interface TextLayout {
  readonly glyphs: readonly PositionedGlyph[];
  readonly lines: readonly LayoutLine[];
  readonly width: number;
  readonly height: number;
}

/** baseline 相对行顶的比例。 */
const ASCENT_RATIO = 0.88;

/**
 * 富文本混合格式（M4-38，用户反馈"选中部分文字改字体/颜色"）的输入单位：
 * 一段连续文字用同一款字体/字号排版——`layoutTextSpans`/`breakLinesSpans`
 * 接受一列 span，各自 shape 再拼成一份布局。单一样式的调用方（`layoutText`/
 * `breakLines`）是"只有一个 span"的特例，内部委托给这套实现，不重复一份逻辑。
 */
export interface StyledSpan {
  readonly text: string;
  readonly shaper: Shaper;
  /** 这个 span 自己的字号（像素/em）——不同 span 字号可以不同。 */
  readonly fontSize: number;
}

interface CharAdvanceInfo {
  readonly chars: readonly string[];
  /** 每个字符所属的 span 下标。 */
  readonly spanIndexAt: readonly number[];
  /** 每个字符自己的字号（来自所属 span）。 */
  readonly fontSizeAt: readonly number[];
  /** 每个字符的有效 advance（像素），含标点挤压与中西文四分空。 */
  readonly advances: readonly number[];
}

/**
 * 每个字符的有效 advance（像素），含标点挤压与中西文四分空。
 * 挤压/间距发生在**字符边界**上，folding 进前一个字符的 advance；
 * 跨 span 边界（比如英文 run 紧跟着中文 run）同样适用——这两条规则
 * 本来就是"相邻字符"层面的判断，不关心它们是不是同一个 span。
 */
function computeCharAdvances(spans: readonly StyledSpan[]): CharAdvanceInfo {
  const chars: string[] = [];
  const spanIndexAt: number[] = [];
  const fontSizeAt: number[] = [];
  const advances: number[] = [];

  spans.forEach((span, spanIndex) => {
    const spanChars = [...span.text];
    const run = span.shaper.shape(span.text);
    const scale = span.fontSize / run.upem;
    const localAdvances = new Array<number>(spanChars.length).fill(0);
    for (const glyph of run.glyphs) {
      if (glyph.cluster < localAdvances.length) localAdvances[glyph.cluster]! += glyph.xAdvance * scale;
    }
    for (const ch of spanChars) {
      chars.push(ch);
      spanIndexAt.push(spanIndex);
      fontSizeAt.push(span.fontSize);
    }
    advances.push(...localAdvances);
  });

  for (let i = 0; i < chars.length; i++) {
    const current = chars[i]!;
    const next = chars[i + 1];
    // 标点挤压：连续约物，后者减半——把减半量提前扣在前者上,
    // 断行时两者通常同侧，误差可接受且实现无状态
    if (next !== undefined && FULLWIDTH_PUNCTUATION.has(current) && FULLWIDTH_PUNCTUATION.has(next)) {
      advances[i + 1] = advances[i + 1]! / 2;
    }
    // 中西文四分空——字号取当前字符自己的（混排字号差异很大时是已知简化）
    if (
      next !== undefined &&
      ((isCjk(current) && isLatinOrDigit(next)) || (isLatinOrDigit(current) && isCjk(next)))
    ) {
      advances[i] = advances[i]! + fontSizeAt[i]! * 0.25;
    }
  }
  return { chars, spanIndexAt, fontSizeAt, advances };
}

/**
 * 禁则字符在行末无处可退时的二级退让（M3-16 / JLReq 的调整方法优先级）。
 *
 * 顺序固定为**压缩优先，悬挂兜底**：
 * 1. 压缩——把约物的视觉留白吃掉一半（全角标点自带的空白本就有冗余，
 *    与标点挤压是同一物理依据），如果压缩后能塞进剩余空间就地收纳；
 * 2. 悬挂——压缩仍不够时，让字符越出行框（JLReq 称"ぶら下げ"，
 *    印刷排版里句读悬挂在页面边缘外是被接受的惯例，不强制换行）。
 *
 * 只对禁则字符生效：普通字符没有"压缩"的物理依据，无处可退就是真断不开，
 * 调用方应继续用强制换行兜底（不属于本函数职责）。
 */
export type OverflowResolution =
  | { readonly kind: 'compress'; readonly factor: number }
  | { readonly kind: 'hang' };

export function resolveKinsokuOverflow(
  char: string,
  advance: number,
  remainingSpace: number,
): OverflowResolution | null {
  if (!KINSOKU_LINE_START.has(char)) return null;
  const factor = 0.5;
  if (advance * factor <= remainingSpace + 1e-6) return { kind: 'compress', factor };
  return { kind: 'hang' };
}

/** 断点合法性：能否在字符 i 之前断行。 */
function canBreakBefore(chars: readonly string[], i: number): boolean {
  if (i <= 0 || i >= chars.length) return false;
  if (KINSOKU_LINE_START.has(chars[i]!)) return false;
  if (KINSOKU_LINE_END.has(chars[i - 1]!)) return false;
  // 拉丁单词内部不断（简化版：两侧都是字母数字即视为词内）
  if (isLatinOrDigit(chars[i - 1]!) && isLatinOrDigit(chars[i]!)) return false;
  return true;
}

/** 折行（富文本混合格式版）：返回每行的 [start, end)，start/end 是拼接后全文的下标。 */
export function breakLinesSpans(spans: readonly StyledSpan[], options: LayoutOptions): LayoutLine[] {
  const { chars, advances } = computeCharAdvances(spans);
  if (chars.length === 0) return [];

  // 显式换行先切段
  const lines: LayoutLine[] = [];
  let paragraphStart = 0;
  const flushParagraph = (start: number, end: number): void => {
    if (start >= end) {
      lines.push({ start, end, width: 0 });
      return;
    }
    if (options.maxWidth === undefined) {
      let width = 0;
      for (let i = start; i < end; i++) width += advances[i]!;
      lines.push({ start, end, width });
      return;
    }
    let lineStart = start;
    let width = 0;
    for (let i = start; i < end; i++) {
      const advance = advances[i]!;
      if (width + advance > options.maxWidth && i > lineStart) {
        // 断点回退到合法位置（避头尾）
        let breakAt = i;
        while (breakAt > lineStart && !canBreakBefore(chars, breakAt)) breakAt--;
        if (breakAt === lineStart) breakAt = i; // 无处可退：强制断（比溢出崩坏好）
        let lineWidth = 0;
        for (let j = lineStart; j < breakAt; j++) lineWidth += advances[j]!;
        lines.push({ start: lineStart, end: breakAt, width: lineWidth });
        lineStart = breakAt;
        // 行首空格吞掉（西文断行惯例）
        while (lineStart < end && chars[lineStart] === ' ') lineStart++;
        width = 0;
        for (let j = lineStart; j <= i; j++) width += advances[j]!;
      } else {
        width += advance;
      }
    }
    let lastWidth = 0;
    for (let j = lineStart; j < end; j++) lastWidth += advances[j]!;
    lines.push({ start: lineStart, end, width: lastWidth });
  };

  for (let i = 0; i <= chars.length; i++) {
    if (i === chars.length || chars[i] === '\n') {
      flushParagraph(paragraphStart, i);
      paragraphStart = i + 1;
    }
  }
  return lines;
}

/**
 * 完整布局（富文本混合格式版）：折行 + 逐行 shaping 定位。
 * `options.fontSize` 只用于行高/baseline 这类"一整行共享一个值"的度量——
 * 调用方（`raster.ts`）传这一行里最大的那个 span 字号，字号差异很大的
 * 混排行会以最大字号定行高，这是常见富文本引擎的近似（不是每个 glyph
 * 精确按自己字号算 baseline 偏移）。每个字符量宽/shaping 仍然用它自己
 * 所属 span 的字号，不受这个近似影响。
 */
export function layoutTextSpans(spans: readonly StyledSpan[], options: LayoutOptions): TextLayout {
  const { chars, advances } = computeCharAdvances(spans);
  const lines = breakLinesSpans(spans, options);
  const glyphs: PositionedGlyph[] = [];
  const lineHeightPx = options.fontSize * options.lineHeight;

  // spanStarts[i] = 第 i 个 span 在拼接后全文里的起始下标
  const spanStarts: number[] = [];
  {
    let acc = 0;
    for (const span of spans) {
      spanStarts.push(acc);
      acc += [...span.text].length;
    }
  }

  for (const [lineIndex, line] of lines.entries()) {
    const baseline = lineIndex * lineHeightPx + options.fontSize * ASCENT_RATIO;
    let x = 0;
    let currentCluster = -1;

    // 这一行跟每个 span 的交集分别 shape——一行可能横跨多个 span
    // （比如"英文 run 后半段"接"中文 run 前半段"折在同一行）
    for (let spanIndex = 0; spanIndex < spans.length; spanIndex++) {
      const spanStart = spanStarts[spanIndex]!;
      const spanEnd = spanStart + [...spans[spanIndex]!.text].length;
      const segStart = Math.max(spanStart, line.start);
      const segEnd = Math.min(spanEnd, line.end);
      if (segStart >= segEnd) continue; // 这个 span 跟这一行没有交集

      const segText = chars.slice(segStart, segEnd).join('');
      if (segText.length === 0) continue;
      const span = spans[spanIndex]!;
      const run = span.shaper.shape(segText);
      const scale = span.fontSize / run.upem;

      // 逐 glyph 铺 x：用含挤压/四分空的字符 advance 表（对齐量宽阶段的度量），
      // glyph 在簇内的相对偏移用 shaping 自己的 advance；currentCluster 按
      // 全局下标持续追踪，跨 span 边界的第一个字符必然触发"新簇"重算 x，
      // 不需要在 span 交界处特殊处理
      for (const glyph of run.glyphs) {
        const globalIndex = segStart + glyph.cluster;
        if (globalIndex !== currentCluster) {
          currentCluster = globalIndex;
          x = 0;
          for (let j = line.start; j < globalIndex; j++) x += advances[j]!;
        }
        glyphs.push({
          glyphId: glyph.glyphId,
          x: x + glyph.xOffset * scale,
          y: baseline - glyph.yOffset * scale,
          cluster: globalIndex,
          spanIndex,
        });
        x += glyph.xAdvance * scale;
      }
    }
  }

  return {
    glyphs,
    lines,
    width: Math.max(0, ...lines.map((l) => l.width)),
    height: lines.length * lineHeightPx,
  };
}

/** 折行：单一样式的特例——只有一个隐式 span，委托给 `breakLinesSpans`。 */
export function breakLines(shaper: Shaper, text: string, options: LayoutOptions): LayoutLine[] {
  return breakLinesSpans([{ text, shaper, fontSize: options.fontSize }], options);
}

/** 完整布局：单一样式的特例——只有一个隐式 span，委托给 `layoutTextSpans`。 */
export function layoutText(shaper: Shaper, text: string, options: LayoutOptions): TextLayout {
  return layoutTextSpans([{ text, shaper, fontSize: options.fontSize }], options);
}
